import { useState, useEffect, useCallback } from "react";
import { AuditLog, AuditActionType, AuditResourceType } from "./types";
import { getMockAuditLogs } from "./mockData";

export interface FilterOptions {
  users: string[];
  actions: AuditActionType[];
  resources: AuditResourceType[];
  dateRange: {
    from: Date | null;
    to: Date | null;
  };
  searchTerm: string;
}

export function useAuditController() {
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<AuditLog[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterOptions>({
    users: [],
    actions: [],
    resources: [],
    dateRange: { from: null, to: null },
    searchTerm: "",
  });

  // Fetch audit logs on component mount
  useEffect(() => {
    const fetchAuditLogs = async () => {
      setIsLoading(true);
      try {
        // In a real app, this would be an API call
        const logs = getMockAuditLogs();
        setAuditLogs(logs);
        setFilteredLogs(logs);
      } catch (err) {
        console.error("Error fetching audit logs:", err);
        setError("Failed to fetch audit logs");
      } finally {
        setIsLoading(false);
      }
    };

    fetchAuditLogs();
  }, []);

  // Apply filters
  const applyFilters = useCallback(() => {
    let filtered = [...auditLogs];

    // Filter by user
    if (filters.users.length > 0) {
      filtered = filtered.filter((log) => filters.users.includes(log.userId));
    }

    // Filter by action
    if (filters.actions.length > 0) {
      filtered = filtered.filter((log) => filters.actions.includes(log.action));
    }

    // Filter by resource
    if (filters.resources.length > 0) {
      filtered = filtered.filter((log) =>
        filters.resources.includes(log.resourceType)
      );
    }

    // Filter by date range
    if (filters.dateRange.from) {
      filtered = filtered.filter(
        (log) => new Date(log.timestamp) >= (filters.dateRange.from as Date)
      );
    }
    if (filters.dateRange.to) {
      filtered = filtered.filter(
        (log) => new Date(log.timestamp) <= (filters.dateRange.to as Date)
      );
    }

    // Filter by search term
    if (filters.searchTerm) {
      const searchTermLower = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(
        (log) =>
          log.userName.toLowerCase().includes(searchTermLower) ||
          log.userEmail.toLowerCase().includes(searchTermLower) ||
          (log.resourceName &&
            log.resourceName.toLowerCase().includes(searchTermLower)) ||
          (log.details && log.details.toLowerCase().includes(searchTermLower))
      );
    }

    setFilteredLogs(filtered);
  }, [auditLogs, filters]);

  // Apply filters whenever filters change
  useEffect(() => {
    applyFilters();
  }, [filters, applyFilters]);

  // Update filters
  const updateFilters = useCallback((newFilters: Partial<FilterOptions>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  // Reset filters
  const resetFilters = useCallback(() => {
    setFilters({
      users: [],
      actions: [],
      resources: [],
      dateRange: { from: null, to: null },
      searchTerm: "",
    });
  }, []);

  // Get unique users for filter dropdown
  const uniqueUsers = useCallback(() => {
    const users = auditLogs.map((log) => ({
      id: log.userId,
      name: log.userName,
      email: log.userEmail,
    }));
    return Array.from(new Map(users.map((user) => [user.id, user])).values());
  }, [auditLogs]);

  return {
    auditLogs: filteredLogs,
    isLoading,
    error,
    filters,
    updateFilters,
    resetFilters,
    uniqueUsers,
  };
}
